import { useEffect, useState } from "react";
import { useAuth } from "./AuthContext";
import { supabase } from "./supabaseClient";

const theme = {
  bg: "#0a0f1a",
  surface: "#111827",
  surfaceHover: "#1a2234",
  border: "#1e2a3a",
  accent: "#00e5a0",
  accentDim: "rgba(0, 229, 160, 0.12)",
  text: "#e2e8f0",
  textMuted: "#64748b",
  textDim: "#475569",
  error: "#f87171",
  warning: "#fbbf24",
};

const STATUS_COLORS = {
  success: theme.accent,
  failed: theme.error,
  flagged: theme.warning,
};

function ResultsTable({ data }) {
  if (!data || !data.columns || data.rows.length === 0) {
    return <p style={{ color: theme.textMuted, fontSize: 13 }}>No results stored.</p>;
  }
  return (
    <div style={{ overflowX: "auto", marginTop: 12 }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
        <thead>
          <tr>
            {data.columns.map((col) => (
              <th key={col} style={{
                textAlign: "left",
                padding: "8px 12px",
                color: theme.accent,
                fontSize: 11,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: 1,
                borderBottom: `1px solid ${theme.border}`,
                background: theme.bg,
                whiteSpace: "nowrap",
              }}>
                {col.replace(/_/g, " ")}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.rows.map((row, i) => (
            <tr key={i}>
              {row.map((cell, j) => (
                <td key={j} style={{
                  padding: "8px 12px",
                  borderBottom: `1px solid ${theme.border}44`,
                  color: theme.text,
                  whiteSpace: "nowrap",
                }}>
                  {cell === null ? <span style={{ color: theme.textDim }}>—</span> : String(cell)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {data.truncated && (
        <p style={{ fontSize: 12, color: theme.textDim, marginTop: 8, padding: "0 12px" }}>
          Results truncated to {data.row_count} rows.
        </p>
      )}
    </div>
  );
}

function rowsToObjects(columns, rows) {
  return rows.map((row) => {
    const obj = {};
    columns.forEach((col, i) => { obj[col] = row[i]; });
    return obj;
  });
}

function HistoryItem({ item, isSelected, onToggleSelect }) {
  const [expanded, setExpanded] = useState(false);
  const [showSql, setShowSql] = useState(false);

  const canSelect =
    onToggleSelect &&
    item.status === "success" &&
    item.results_summary?.rows?.length > 0;

  return (
    <div style={{
      background: theme.surface,
      border: `1px solid ${theme.border}`,
      borderRadius: 12,
      overflow: "hidden",
      marginBottom: 10,
    }}>
      {/* Header row */}
      <div
        onClick={() => setExpanded(!expanded)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: "14px 16px",
          cursor: "pointer",
          background: expanded ? theme.surfaceHover : "transparent",
          transition: "background 0.15s",
        }}
      >
        <span style={{
          fontSize: 11,
          fontWeight: 700,
          color: STATUS_COLORS[item.status] || theme.textMuted,
          textTransform: "uppercase",
          letterSpacing: 0.8,
          minWidth: 52,
        }}>
          {item.status}
        </span>
        <span style={{ flex: 1, fontSize: 14, color: theme.text }}>
          {item.user_prompt}
        </span>
        <span style={{ fontSize: 12, color: theme.textDim, whiteSpace: "nowrap" }}>
          {new Date(item.created_at).toLocaleDateString(undefined, {
            month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
          })}
        </span>
        {canSelect && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggleSelect({
                id: item.id,
                prompt: item.user_prompt,
                sql: item.generated_sql,
                results: rowsToObjects(
                  item.results_summary.columns,
                  item.results_summary.rows
                ),
              });
            }}
            style={{
              background: isSelected ? theme.accentDim : "transparent",
              border: `1px solid ${isSelected ? theme.accent : theme.border}`,
              borderRadius: 6,
              padding: "3px 9px",
              color: isSelected ? theme.accent : theme.textMuted,
              fontSize: 11,
              fontFamily: "'IBM Plex Mono', monospace",
              cursor: "pointer",
              transition: "all 0.15s",
              whiteSpace: "nowrap",
            }}
          >
            {isSelected ? "✓" : "+"}
          </button>
        )}
        <span style={{ color: theme.textMuted, fontSize: 12 }}>{expanded ? "▲" : "▼"}</span>
      </div>

      {/* Expanded body */}
      {expanded && (
        <div style={{ padding: "0 16px 16px", borderTop: `1px solid ${theme.border}` }}>
          {item.generated_sql && (
            <div style={{ marginTop: 12 }}>
              <button
                onClick={() => setShowSql(!showSql)}
                style={{
                  background: "none",
                  border: "none",
                  color: theme.textMuted,
                  fontSize: 12,
                  cursor: "pointer",
                  fontFamily: "'IBM Plex Mono', monospace",
                  padding: 0,
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <span style={{ fontSize: 10 }}>{showSql ? "▼" : "▶"}</span>
                {showSql ? "Hide" : "Show"} SQL
              </button>
              {showSql && (
                <div style={{
                  marginTop: 8,
                  padding: "10px 12px",
                  background: theme.bg,
                  borderRadius: 8,
                  fontSize: 12,
                  color: theme.accent,
                  fontFamily: "'IBM Plex Mono', monospace",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}>
                  {item.generated_sql}
                </div>
              )}
            </div>
          )}
          {item.results_summary && (
            <ResultsTable data={item.results_summary} />
          )}
        </div>
      )}
    </div>
  );
}

export default function HistoryPanel({ selectedQueryIds = [], onToggleSelect }) {
  const { user } = useAuth();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    if (user) fetchHistory();
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchHistory = async () => {
    setLoading(true);
    setError(null);
    const { data, error: err } = await supabase
      .from("query_history")
      .select("*")
      .order("created_at", { ascending: false });
    if (err) {
      setError("Failed to load history.");
    } else {
      setHistory(data || []);
    }
    setLoading(false);
  };

  const filtered = history
    .filter((h) => statusFilter === "all" || h.status === statusFilter)
    .filter((h) => h.user_prompt.toLowerCase().includes(search.toLowerCase()));

  if (!user) {
    return (
      <div style={{
        textAlign: "center",
        padding: "64px 24px",
        color: theme.textMuted,
        fontSize: 14,
        fontFamily: "'IBM Plex Mono', monospace",
      }}>
        Sign in to save and view your query history.
      </div>
    );
  }

  return (
    <div style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
      {/* Filters */}
      <div style={{ display: "flex", gap: 10, marginBottom: 20, flexWrap: "wrap" }}>
        <input
          type="text"
          placeholder="Search queries..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            flex: 1,
            minWidth: 200,
            padding: "10px 14px",
            background: theme.surface,
            border: `1px solid ${theme.border}`,
            borderRadius: 8,
            color: theme.text,
            fontSize: 13,
            fontFamily: "'IBM Plex Mono', monospace",
            outline: "none",
          }}
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={{
            padding: "10px 14px",
            background: theme.surface,
            border: `1px solid ${theme.border}`,
            borderRadius: 8,
            color: theme.textMuted,
            fontSize: 13,
            fontFamily: "'IBM Plex Mono', monospace",
            cursor: "pointer",
            outline: "none",
          }}
        >
          <option value="all">All statuses</option>
          <option value="success">Success</option>
          <option value="failed">Failed</option>
          <option value="flagged">Flagged</option>
        </select>
        <button
          onClick={fetchHistory}
          style={{
            padding: "10px 16px",
            background: theme.accentDim,
            border: `1px solid ${theme.accent}33`,
            borderRadius: 8,
            color: theme.accent,
            fontSize: 13,
            fontFamily: "'IBM Plex Mono', monospace",
            cursor: "pointer",
          }}
        >
          Refresh
        </button>
      </div>

      {/* Content */}
      {loading && (
        <p style={{ color: theme.textMuted, fontSize: 13, textAlign: "center", padding: "32px 0" }}>
          Loading...
        </p>
      )}
      {error && (
        <p style={{ color: theme.error, fontSize: 13 }}>{error}</p>
      )}
      {!loading && !error && filtered.length === 0 && (
        <p style={{ color: theme.textMuted, fontSize: 13, textAlign: "center", padding: "32px 0" }}>
          {history.length === 0 ? "No history yet. Start asking questions!" : "No results match your filters."}
        </p>
      )}
      {filtered.map((item) => (
        <HistoryItem
          key={item.id}
          item={item}
          isSelected={selectedQueryIds.includes(item.id)}
          onToggleSelect={onToggleSelect}
        />
      ))}
    </div>
  );
}
